// pages/admin/AdminCouponUsers.tsx

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Avatar,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Chip,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Refresh,
  NavigateNext,
  Home,
  Search,
  Person,
  LocalOffer,
  Email,
  Phone,
  CalendarToday,
  Discount,
  AttachMoney,
  CheckCircle,
  Cancel,
  Receipt,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListAdminCouponUsersQuery,
  CouponUser,
  STATUS_OPTIONS,
  DISCOUNT_TYPE_OPTIONS,
} from '../../features/admin/adminCouponUsersApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
  gold: '#ffd700',
  orange: '#ff6b35',
};

const AdminCouponUsers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | ''>('');
  const [discountTypeFilter, setDiscountTypeFilter] = useState<string | ''>('');

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListAdminCouponUsersQuery();

  // Handle API response structure
  const couponUsers = data?.data || [];
  const totalRecords = couponUsers.length;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRefresh = () => {
    refetch();
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view coupon users.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No coupon users found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load coupon users. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusChip = (status: number) => {
    const statusOption = STATUS_OPTIONS.find(opt => opt.value === status);
    return (
      <Chip
        icon={status === 1 ? <CheckCircle sx={{ fontSize: 16 }} /> : <Cancel sx={{ fontSize: 16 }} />}
        label={statusOption?.label || 'Unknown'}
        size="small"
        sx={{
          borderRadius: '6px',
          fontWeight: 600,
          backgroundColor: status === 1 ? `${shopifyColors.success}20` : `${shopifyColors.error}20`,
          color: status === 1 ? shopifyColors.success : shopifyColors.error,
          fontSize: '0.75rem',
        }}
      />
    );
  };

  const getDiscountTypeChip = (type: string) => {
    const isPercentage = type === 'percentage';
    return (
      <Chip
        label={isPercentage ? 'Percentage' : 'Fixed'}
        size="small"
        sx={{
          borderRadius: '6px',
          fontWeight: 600,
          backgroundColor: isPercentage ? `${shopifyColors.blue}20` : `${shopifyColors.purple}20`,
          color: isPercentage ? shopifyColors.blue : shopifyColors.purple,
          fontSize: '0.75rem',
        }}
      />
    );
  };

  // Filter coupons based on search and filters
  const filteredCouponUsers = couponUsers.filter((coupon: CouponUser) => {
    const matchesSearch = !searchStr || 
      coupon.first_name?.toLowerCase().includes(searchStr.toLowerCase()) ||
      coupon.last_name?.toLowerCase().includes(searchStr.toLowerCase()) ||
      coupon.user_email?.toLowerCase().includes(searchStr.toLowerCase()) ||
      coupon.code?.toLowerCase().includes(searchStr.toLowerCase()) ||
      coupon.user_contact?.includes(searchStr);
    
    const matchesStatus = statusFilter === '' || coupon.status === statusFilter;
    const matchesDiscountType = discountTypeFilter === '' || coupon.discount_type === discountTypeFilter;
    
    return matchesSearch && matchesStatus && matchesDiscountType;
  });

  // Calculate statistics
  const activeCount = couponUsers.filter((c: CouponUser) => c.status === 1).length;
  const inactiveCount = couponUsers.filter((c: CouponUser) => c.status === 0).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link color="inherit" href="#" underline="hover">
                <Home sx={{ fontSize: 20 }} />
              </Link>
              <Link color="inherit" href="#" underline="hover">
                Marketing
              </Link>
              <Typography color={shopifyColors.textPrimary} sx={{ fontWeight: 600 }}>
                Coupon Users
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  Coupon Users
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  View all users who have been assigned promotional coupons
                </Typography>
              </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    backgroundColor: shopifyColors.surface,
                    borderRadius: '8px',
                    border: `1px solid ${shopifyColors.border}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '8px',
                        backgroundColor: `${shopifyColors.blue}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <LocalOffer sx={{ fontSize: 24, color: shopifyColors.blue }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                        {totalRecords}
                      </Typography>
                      <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                        Total Assignments
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    backgroundColor: shopifyColors.surface,
                    borderRadius: '8px',
                    border: `1px solid ${shopifyColors.border}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '8px',
                        backgroundColor: `${shopifyColors.success}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 24, color: shopifyColors.success }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                        {activeCount}
                      </Typography>
                      <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                        Active Coupons
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    backgroundColor: shopifyColors.surface,
                    borderRadius: '8px',
                    border: `1px solid ${shopifyColors.border}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '8px',
                        backgroundColor: `${shopifyColors.error}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Cancel sx={{ fontSize: 24, color: shopifyColors.error }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                        {inactiveCount}
                      </Typography>
                      <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                        Inactive Coupons
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    backgroundColor: shopifyColors.surface,
                    borderRadius: '8px',
                    border: `1px solid ${shopifyColors.border}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '8px',
                        backgroundColor: `${shopifyColors.purple}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Person sx={{ fontSize: 24, color: shopifyColors.purple }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                        {new Set(couponUsers.map((c: CouponUser) => c.user_id)).size}
                      </Typography>
                      <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                        Unique Users
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2, overflow: 'auto' }}>
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Status Filter */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as number | '')}
                      label="Status"
                      sx={{ borderRadius: '6px' }}
                    >
                      <MenuItem value="">
                        <em>All Statuses</em>
                      </MenuItem>
                      {STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Discount Type Filter */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Discount Type</InputLabel>
                    <Select
                      value={discountTypeFilter}
                      onChange={(e) => setDiscountTypeFilter(e.target.value as string)}
                      label="Discount Type"
                      sx={{ borderRadius: '6px' }}
                    >
                      <MenuItem value="">
                        <em>All Types</em>
                      </MenuItem>
                      {DISCOUNT_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Search */}
                  <TextField
                    size="small"
                    placeholder="Search by name, email, code..."
                    value={searchStr}
                    onChange={(e) => setSearchStr(e.target.value)}
                    sx={{
                      minWidth: 250,
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {filteredCouponUsers.length} coupon assignment{filteredCouponUsers.length === 1 ? '' : 's'}
                {searchStr && ` matching "${searchStr}"`}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading coupon users...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRefresh}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredCouponUsers.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <LocalOffer sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No coupon users found' : 'No coupon assignments yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                  {searchStr 
                    ? `No assignments match your search "${searchStr}"`
                    : 'Coupon assignments will appear here once users are assigned coupons'
                  }
                </Typography>
              </Card>
            )}

            {/* Coupon Users List */}
            {!isLoading && filteredCouponUsers.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {filteredCouponUsers.map((coupon: CouponUser, index: number) => (
                    <ListItem
                      key={coupon.id}
                      sx={{
                        borderBottom: index < filteredCouponUsers.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                        '&:hover': {
                          backgroundColor: `${shopifyColors.background}80`,
                        },
                        py: 2.5,
                        px: 3,
                        display: 'flex',
                        alignItems: 'flex-start',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 56 }}>
                        <Avatar
                          sx={{
                            bgcolor: shopifyColors.orange,
                            width: 48,
                            height: 48,
                          }}
                        >
                          <LocalOffer />
                        </Avatar>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                              {coupon.first_name} {coupon.last_name}
                            </Typography>
                            
                            <Chip
                              label={coupon.code}
                              size="small"
                              icon={<Receipt sx={{ fontSize: 14 }} />}
                              sx={{
                                borderRadius: '6px',
                                fontWeight: 700,
                                backgroundColor: `${shopifyColors.primary}20`,
                                color: shopifyColors.primary,
                                fontSize: '0.75rem',
                              }}
                            />
                            
                            {getStatusChip(coupon.status)}
                            {getDiscountTypeChip(coupon.discount_type)}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1.5 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Email sx={{ fontSize: 16, color: shopifyColors.textSecondary }} />
                                    <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                      {coupon.user_email}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Phone sx={{ fontSize: 16, color: shopifyColors.textSecondary }} />
                                    <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                      {coupon.user_contact}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Discount sx={{ fontSize: 16, color: shopifyColors.blue }} />
                                    <Typography variant="body2" sx={{ color: shopifyColors.textPrimary, fontWeight: 600 }}>
                                      {coupon.discount_type === 'percentage' 
                                        ? `${coupon.discount_value}% off` 
                                        : `$${coupon.discount_value} off`
                                      }
                                    </Typography>
                                    {coupon.max_discount && (
                                      <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                                        (Max: ${coupon.max_discount})
                                      </Typography>
                                    )}
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarToday sx={{ fontSize: 16, color: shopifyColors.textSecondary }} />
                                    <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                      {formatDate(coupon.start_date)} - {formatDate(coupon.end_date)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pt: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                                    User ID: {coupon.user_id}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                                    Coupon ID: {coupon.coupon_id}
                                  </Typography>
                                  {coupon.order_id && (
                                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                                      Order ID: {coupon.order_id}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                                    Assigned: {formatDate(coupon.selected_at)}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminCouponUsers;